import * as React from "react";
import { useEffect } from "react";
import { CALL_STATUS_COLORS, CALL_STATUS_LABELS, STACK_STATUSES } from "@/constants";
import { AnalysingIndicator } from "@/new-pages/dashboard/campaigns/view/lead-list/analysing-indicator";
import { downloadFileFromResponse, formatDateWithTimezone, formatDuration } from "@/utils";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { ChatsIcon, DownloadIcon, InfoIcon, PlayCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CallLog, useMarkCallStuck } from "@/hooks/api/use-call-logs";
import { Campaign } from "@/hooks/api/use-campaigns";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CallLogModal } from "@/components/call-log-modal/call-log-modal";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption } from "@/components/customTable/types";
import { RecordingModal } from "@/components/recording-modal/recording-modal";

import { useAuth } from "../core/auth-provider";
import { toast } from "../core/toaster";
import { ConfirmModal } from "../confirm-modal/confirm-modal";
import { CompletionsJsonModal } from "@/new-pages/dashboard/call-logs/completions-json-modal";
import { API_URL, BASE_URL } from "@/runtime-env";
import { appHref, appPathname } from "@/lib/platform";

export function CallLogs({
	queryKey,
	endpoint,
	filtersOptions,
	hideCampaign = false,
	hideAgents = false,
	showExportButton = false,
	campaign = null,
	tableHeaderTitle = "Call Logs",
}: {
	queryKey: readonly string[];
	endpoint: string;
	filtersOptions: FilterOption[];
	hideCampaign?: boolean;
	hideAgents?: boolean;
	showExportButton?: boolean;
	campaign?: Campaign | null;
	tableHeaderTitle?: string;
}): React.JSX.Element {
	const { user, permissions } = useAuth();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [isExportingCalls, setIsExportingCalls] = React.useState(false);
	const [isExportingCallsAdmin, setIsExportingCallsAdmin] = React.useState(false);
	const [stuckCallId, setStuckCallId] = React.useState<string | null>(null);
	const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [selectedConversationId, setSelectedConversationId] = React.useState<string | null>(null);

	const {
		data: selectedRecordingCallLog,
		open: isRecordingModalOpen,
		handleClose: handleRecordingModalClose,
		handleOpen: handleRecordingModalOpen,
	} = useDialog<CallLog | null>();

	const {
		items: callLogs,
		isLoading,
		totalItems,
		refetchData: fetchCallLogs,
	} = useCachedTableStateWithUrl<CallLog>({
		queryKey: queryKey,
		endpoint: endpoint,
	});

	const {
		data: selectedCallLog,
		open: isCallLogModalOpen,
		handleClose: handleCallLogModalClose,
		handleOpen: handleCallLogModalOpen,
	} = useDialog<CallLog | null>();

  const markCallStuckMutation = useMarkCallStuck();

	const handleExportCampaignCalls = async (isAdmin = false) => {
		try {
			if (isAdmin) {
				setIsExportingCallsAdmin(true);
			} else {
				setIsExportingCalls(true);
			}

			const token = localStorage.getItem("token");
			if (!token) {
				toast.error("Authentication token not found");
				return;
			}


			// const exportRedirectUrl = `${BASE_URL}/campaigns/${campaignId}/view`;
			const exportRedirectUrl = `${BASE_URL}${appHref("/call-logs")}`;

			const response = await fetch(
				`${API_URL}/campaigns/${campaign?.id}/export?url=${exportRedirectUrl}${isAdmin ? "&withConversation=true" : ""}`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Export failed: ${response.statusText}`);
			}

			await downloadFileFromResponse(response);

			toast.success("Campaign calls exported successfully");
		} catch (error: any) {
			const errorMessage = error?.message || "Campaign calls export failed";
			toast.error(errorMessage);
		} finally {
			if (isAdmin) {
				setIsExportingCallsAdmin(false);
			} else {
				setIsExportingCalls(false);
			}
		}
	};

	useEffect(() => {
		const callId = searchParams.get("play");

		if (callId) {
			handleRecordingModalOpen({ id: callId } as CallLog);
			navigate(appPathname(), { replace: true });
		}
	}, []);

	const isSuperAdmin = user?.isSuperAdmin;

	const renderHeaderButtons = () => {
		return (
			<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
				<Button
					startIcon={<DownloadIcon />}
					variant="contained"
					color="info"
					disabled={isExportingCalls || !campaign}
					loading={isExportingCalls || !campaign}
					onClick={() => {
						handleExportCampaignCalls();
					}}
				>
					Export Calls
				</Button>
				{isSuperAdmin && (
					<Button
						startIcon={<DownloadIcon />}
						variant="contained"
						color="primary"
						disabled={isExportingCallsAdmin || !campaign}
						loading={isExportingCallsAdmin || !campaign}
						onClick={() => {
							handleExportCampaignCalls(true);
						}}
					>
						Export Calls Admin
					</Button>
				)}
			</Box>
		);
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
				getCellValue: (row: any) => {
					return (
						<Tooltip title={row.id} arrow>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontWeight: "600",
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									maxWidth: "100px",
								}}
							>
								{row.id}
							</Typography>
						</Tooltip>
					);
				},
			},
			...(hideCampaign
				? []
				: [
						{
							header: {
								id: "campaign",
								label: "Campaign",
								align: "left",
								sx: {
									fontWeight: "600",
								},
							},
							accessorKey: "campaign",
							getCellValue: (row: any) => {
								if (!row.campaign) return "";
								return (
									<Tooltip title="View Campaign" arrow>
										<Chip
											color="info"
											size="small"
											label={row.campaign?.title}
											onClick={() => navigate(`/campaigns/${row.campaign?.id}/view`)}
										/>
									</Tooltip>
								);
							},
						},
					]),
			...(hideAgents
				? []
				: [
						{
							header: {
								id: "agent",
								label: "Agent",
								align: "left",
								sx: {
									fontWeight: "600",
								},
							},
							accessorKey: "agent",
							getCellValue: (row: any) => {
								if (!row.agent) return "";
								return (
									<Tooltip title="View Agent" arrow>
										<Chip
											color="primary"
											size="small"
											label={row.agent?.name}
											onClick={() => navigate(`/agents/${row.agent?.id}`)}
										/>
									</Tooltip>
								);
							},
						},
					]),
			{
				header: {
					id: "contactNumber",
					label: "Contact Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "contactNumber",
			},
			{
				header: {
					id: "originNumber",
					label: "Origin Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "originNumber",
			},
			{
				header: {
					id: "duration",
					label: "Duration",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "duration",
				getCellValue: (row: any) => (
					<Typography variant="body2" whiteSpace="nowrap">
						{formatDuration(row.duration)}
					</Typography>
				),
			},
			{
				header: {
					id: "direction",
					label: "Direction",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "direction",
				getCellValue: (row: any) => {
					return (
						<Chip
							color="primary"
							variant={row.isOutbound ? "outlined" : "filled"}
							size="small"
							label={row.isOutbound ? "outbound" : "inbound"}
						/>
					);
				},
			},
			{
				header: {
					id: "status",
					label: "Status",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "status",
				getCellValue: (row: any) => {
					return (
						<Tooltip title={row?.metadata?.reason} arrow>
							<Chip
								color={CALL_STATUS_COLORS[row.status]}
								variant="outlined"
								size="small"
								label={CALL_STATUS_LABELS[row.status]}
							/>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "summary",
					label: "Summary",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "summary",
				getCellValue: (row: any) => {
					if (row?.metadata?.enqueuedForAnalysis) {
						return <AnalysingIndicator />;
					}

					const summary: string | undefined = row?.metadata?.summary;
					if (!summary) {
						return (
							<Typography variant="body2" color="text.secondary">
								—
							</Typography>
						);
					}

					return (
						<Tooltip
							title={summary}
							arrow
							slotProps={{ tooltip: { sx: { lineHeight: 1.8, maxWidth: 400 } } }}
						>
							<Typography
								variant="body2"
								sx={{
									maxWidth: 280,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									cursor: "default",
								}}
							>
								{summary}
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "disposition",
					label: "Disposition",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "disposition",
				getCellValue: (row: any) => {
					const disposition: string | undefined = row?.metadata?.disposition;
					if (!disposition) {
						return (
							<Typography variant="body2" color="text.secondary">
								—
							</Typography>
						);
					}
					return <Chip label={disposition} size="small" variant="outlined" color="primary" />;
				},
			},
			{
				header: {
					id: "sentiment",
					label: "Sentiment",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "sentiment",
				getCellValue: () => {
					return (
						<Tooltip title="Sentiment scoring coming soon" arrow>
							<Typography variant="body2" color="text.disabled">
								—
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "createdAt",
					label: "Date",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "createdAt",
				getCellValue: (row: any) => formatDateWithTimezone(row.createdAt, campaign?.timezone),
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => {
					const hasStuckAction = STACK_STATUSES.includes(row?.status) && permissions?.unstuck;
					const hasConversationAction = row?.conversationId;

					const allActionsAreHidden = !hasConversationAction && !hasStuckAction;

					if (allActionsAreHidden) {
						return null;
					}

					return (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
							<TableActions
								actions={[
									{
										hidden: !hasConversationAction,
										label: "Play recording",
										icon: <PlayCircleIcon width={20} height={20} weight="fill" color="var(--mui-palette-info-light)" />,
										onClick: () => handleRecordingModalOpen(row),
									},
									{
										hidden: !hasConversationAction,
										label: "View conversation logs",
										icon: <ChatsIcon width={20} height={20} weight="fill" color="var(--mui-palette-primary-main)" />,
										onClick: () => handleCallLogModalOpen(row),
									},
                  {
										hidden: !user?.isSuperAdmin,
										label: "View completions JSON",
										icon: <InfoIcon width={20} height={20} weight="fill" color="var(--mui-palette-info-main)" />,
										onClick: () => setSelectedConversationId(String(row.conversationId)),
									},
									{
										hidden: !hasStuckAction,
										label: "Mark as stuck",
										icon: (
											<WarningIcon
												width={20}
												height={20}
												weight="fill"
												color="var(--mui-palette-warning-main)"
											/>
										),
										onClick: () => {
											setStuckCallId(row.id);
											setIsConfirmOpen(true);
										},
									},
								]}
							/>
						</Box>
					);
				},
			},
		];
	}, [
		hideCampaign,
		hideAgents,
		navigate,
		campaign?.timezone,
		permissions?.unstuck,
		handleRecordingModalOpen,
		handleCallLogModalOpen,
	]);

	return (
		<>
			<CustomTable
				columns={columns}
				data={callLogs}
				refetchData={fetchCallLogs}
				isLoading={isLoading}
				totalItems={totalItems}
				filtersOptions={filtersOptions}
				tableHeaderTitle={tableHeaderTitle}
				renderHeaderButtons={showExportButton ? renderHeaderButtons : undefined}
			/>
			<RecordingModal
				open={isRecordingModalOpen}
				onClose={handleRecordingModalClose}
				callLog={selectedRecordingCallLog || null}
			/>
			<CallLogModal open={isCallLogModalOpen} onClose={handleCallLogModalClose} callLog={selectedCallLog || null} />

			<ConfirmModal
				open={isConfirmOpen}
				onClose={() => {
					setIsConfirmOpen(false);
					setStuckCallId(null);
				}}
				onSumbit={async () => {
					if (!stuckCallId) return;
					await markCallStuckMutation.mutateAsync(stuckCallId);
					setStuckCallId(null);
					fetchCallLogs();
				}}
				title="Mark call as stuck"
				description="Are you sure you want to mark this call as stuck?"
			/>
      <CompletionsJsonModal
				open={Boolean(selectedConversationId)}
				conversationId={selectedConversationId}
				onClose={() => setSelectedConversationId(null)}
			/>
		</>
	);
}
